import { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { api } from "../lib/apiClient";
import { useApp, MONTHS } from "../lib/store";
import { PageHeader, StatusBadge, Empty, StatCard } from "../components/Shared";
import { useEmployees, EmployeePicker } from "../components/Filters";
import { CONTENT_STATUSES } from "../lib/constants";
import { toast } from "sonner";
import { CalendarCheck2, Clock, AlertTriangle, Gauge } from "lucide-react";

const CONTENT_EMP = ["om", "monish", "shashank", "anjali", "hani"];

export default function ContentCalendar() {
  const { month, year, can, isEmployee, selfEmployeeId } = useApp();
  const employees = useEmployees();
  const [empId, setEmpId] = useState(isEmployee ? selfEmployeeId : "all");
  const [status, setStatus] = useState("all");
  const [brand, setBrand] = useState("all");
  const [brands, setBrands] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [agg, setAgg] = useState(null);
  const [modal, setModal] = useState(null);

  const contentEmps = employees.filter((e) => CONTENT_EMP.includes(e.id));
  const nameOf = (id) => employees.find((e) => e.id === id)?.name || id;

  useEffect(() => { api.brands().then(setBrands).catch(() => {}); }, []);

  const load = () => {
    api.contentTasks({ employee_id: empId, month, year, status }).then((t) => {
      setTasks(brand === "all" ? t : t.filter((x) => x.brand === brand));
    }).catch(() => {});
    if (empId !== "all") api.contentTasks({ employee_id: empId, month, year }).then((t) => {
      const total = t.length, on = t.filter((x) => x.status === "Completed" && (x.delay_days || 0) <= 0).length;
      const del = t.filter((x) => (x.delay_days || 0) > 0 || x.status === "Delayed").length;
      const avg = total ? t.reduce((s, x) => s + (x.auto_score || 0), 0) / total : 0;
      setAgg({ total, on, del, avg: avg.toFixed(2) });
    });
    else setAgg(null);
  };
  useEffect(() => { load(); }, [empId, month, year, status, brand]);

  const changeStatus = (t, s) => {
    const body = { status: s };
    if (s === "Completed" && !t.actual_completion_date) body.actual_completion_date = new Date().toISOString();
    api.updateContentTask(t.id, body).then(() => { toast.success(`Status → ${s}`); load(); });
  };

  return (
    <div>
      <PageHeader title="Content Calendar Tracker" subtitle="Mandatory deliverables · deadline-driven auto scoring"
        exportModule="content-calendar" exportParams={{ employee_id: empId, month, year }}>
        {can.assignTasks && <button data-testid="add-content-task-button" onClick={() => setModal({ status: "Pending", employee_id: contentEmps[0]?.id, month, year })} className="pill px-3 py-2 text-sm font-semibold brand-bg text-white"><Plus size={15} /> New Task</button>}
      </PageHeader>

      {agg && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <StatCard label="Total Tasks" value={agg.total} icon={CalendarCheck2} accent="#2563EB" />
          <StatCard label="On Time" value={agg.on} icon={Clock} accent="#10B981" />
          <StatCard label="Delayed" value={agg.del} icon={AlertTriangle} accent="#EF4444" />
          <StatCard label="Auto KRA Score" value={agg.avg} sub="weighted avg / 10" icon={Gauge} accent="#818CF8" />
        </div>
      )}

      <div className="card-panel p-4 mb-5 flex flex-wrap gap-3 items-center">
        <EmployeePicker value={empId} onChange={setEmpId} employees={contentEmps} includeAll={!isEmployee} disabled={isEmployee} testid="cc-employee-filter" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-c" style={{ maxWidth: 170 }} data-testid="cc-status-filter">
          <option value="all">All Statuses</option>
          {CONTENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={brand} onChange={(e) => setBrand(e.target.value)} className="input-c" style={{ maxWidth: 190 }} data-testid="cc-brand-filter">
          <option value="all">All Brands</option>
          {brands.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
        </select>
        <span className="text-xs text-muted-c ml-auto">{MONTHS.find((m) => m.v === month)?.n} {year}</span>
      </div>

      <div className="card-panel overflow-x-auto" data-testid="cc-table">
        <table className="data-table">
          <thead><tr><th>Employee</th><th>Brand</th><th>Type</th><th>Task</th><th>Required</th><th>Delivery Date</th><th>Status</th><th>Delay</th><th>Score</th>{can.updateTasks && <th></th>}</tr></thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id} data-testid={`cc-row-${t.id}`}>
                <td className="font-medium">{nameOf(t.employee_id)}</td>
                <td className="text-secondary">{t.brand}</td>
                <td className="text-secondary text-xs">{t.content_type}</td>
                <td className="max-w-[200px] truncate">{t.task_title}</td>
                <td className="mono text-xs">{(t.required_completion_date || "").slice(0, 10)}</td>
                <td className="mono text-xs">{(t.actual_completion_date || "—").slice(0, 10)}</td>
                <td>
                  {can.updateTasks ? (
                    <select value={t.status} onChange={(e) => changeStatus(t, e.target.value)} className="input-c py-1 text-xs" style={{ width: 130 }} data-testid={`task-status-dropdown-${t.id}`}>
                      {CONTENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  ) : <StatusBadge status={t.status} />}
                </td>
                <td className="mono text-xs">{t.delay_days > 0 ? <span className="text-red-500">+{t.delay_days}d</span> : <span className="text-emerald-500">0</span>}</td>
                <td className="mono font-bold">{t.auto_score}</td>
                {can.updateTasks && <td><button onClick={() => { if (window.confirm("Delete task?")) api.deleteContentTask(t.id).then(() => { toast.success("Deleted"); load(); }); }} className="text-muted-c hover:text-red-500"><Trash2 size={15} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!tasks.length && <Empty text="No content calendar tasks for this selection" />}
      </div>

      {modal && <TaskModal task={modal} emps={contentEmps} brands={brands} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function TaskModal({ task, emps, brands, onClose, onSaved }) {
  const [f, setF] = useState({
    employee_id: task.employee_id, brand: brands[0]?.name || "", content_type: "Instagram Post", task_title: "",
    task_description: "", posting_date: "", required_completion_date: "", actual_completion_date: "",
    status: "Pending", month: task.month, year: task.year,
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!f.task_title) return toast.error("Task title required");
    const body = { ...f };
    ["posting_date", "required_completion_date", "actual_completion_date"].forEach((k) => { if (body[k]) body[k] = new Date(body[k]).toISOString(); else body[k] = null; });
    await api.createContentTask(body); toast.success("Task created"); onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="card-panel w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="cc-task-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-subtle"><h3 className="font-display font-semibold">New Content Task</h3><button onClick={onClose}><X size={18} className="text-muted-c" /></button></div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <F l="Employee"><select className="input-c" value={f.employee_id} onChange={(e) => set("employee_id", e.target.value)}>{emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select></F>
            <F l="Brand"><select className="input-c" value={f.brand} onChange={(e) => set("brand", e.target.value)} data-testid="cc-brand-input">{brands.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}</select></F>
          </div>
          <F l="Task Title"><input className="input-c" value={f.task_title} onChange={(e) => set("task_title", e.target.value)} data-testid="cc-title-input" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F l="Content Type"><input className="input-c" value={f.content_type} onChange={(e) => set("content_type", e.target.value)} /></F>
            <F l="Status"><select className="input-c" value={f.status} onChange={(e) => set("status", e.target.value)}>{CONTENT_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F l="Required Date"><input type="date" className="input-c" value={f.required_completion_date} onChange={(e) => set("required_completion_date", e.target.value)} /></F>
            <F l="Delivery Date"><input type="date" className="input-c" value={f.actual_completion_date} onChange={(e) => set("actual_completion_date", e.target.value)} /></F>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-subtle"><button onClick={onClose} className="pill px-4 py-2 text-sm border-strong">Cancel</button><button onClick={save} className="pill px-4 py-2 text-sm brand-bg text-white font-semibold" data-testid="save-cc-task-button">Create</button></div>
      </div>
    </div>
  );
}
function F({ l, children }) { return <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">{l}</label>{children}</div>; }
